import type { BooleanOrPredicate } from '../types/types.utils';
import { allowedColors } from '@components/atoms/tag/types';
import type { TAllowedColors } from 'src/components';

const IS_ANDROID_OR_IPHONE_REGEX: RegExp = /Android|iPhone/i;
const IS_TABLET_REGEX: RegExp = /iPad|Tablet/i;

/**
 * Check if the user is on a mobile phone.
 * @returns {boolean} - True if the user is on a mobile phone, false otherwise.
 */
export function isMobilePhone(): boolean {
	if (typeof navigator === 'undefined') {
		return false;
	}

	const userAgent: string = navigator.userAgent;

	const isMobile: boolean = IS_ANDROID_OR_IPHONE_REGEX.test(userAgent);
	const isTablet: boolean = IS_TABLET_REGEX.test(userAgent);

	return isMobile && !isTablet;
}

/**
 * Evaluate a value that may be a boolean literal or a predicate function.
 * If the input is undefined, the provided default value is returned.
 *
 * @param input - input to evaluate, either a boolean or a predicate function
 * @param value - value to call predicate with, should input be a function
 * @param valueIfInputUndefined - fallback value to return if input is undefined
 * @returns - boolean representing the input or evaluated input
 */
export function evaluateBooleanOrPredicate<T>(
	input: BooleanOrPredicate<T> | undefined,
	value: T,
	valueIfInputUndefined: boolean = false
): boolean {
	if (typeof input === 'function') return (input as (v: T) => boolean)(value);
	return input ?? valueIfInputUndefined;
}

/**
 * Get a random color based on a string.
 * @param str - string to generate a color from
 * @returns - random color
 */
export function getRandomColor(str: string): TAllowedColors {
	let hash = 0;

	for (let i = 0; i < str.length; i++) {
		hash = str.charCodeAt(i) + ((hash << allowedColors.length) - hash);
	}

	return allowedColors[Math.abs(hash) % allowedColors.length] as TAllowedColors;
}
